import React, { Suspense, useState } from 'react'
import { Outlet, useLocation, useMatch, useNavigate, useParams } from 'react-router'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Drawer, { DrawerView } from '@divvy-web/skylab.drawer'
import Spinner from '@divvy-web/skylab.spinner'
import Tab from '@divvy-web/skylab.tab'

import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import { useGetCreditApplicationDetails } from './GetCreditApplicationDetails.gql'
import styles from './styles.module.scss'

const DashboardDetailsDrawer = () => {
  const { appId } = useParams()
  const { pathname } = useLocation()

  const isIndexMatch = useMatch('/dashboard/:appId')
  const isDetailsTabMatch = useMatch('/dashboard/:appId/details')
  const isDocumentsTabMatch = useMatch('/dashboard/:appId/documents')
  const navigate = useNavigate()

  const salesforceCreditId = appId ?? ''
  const isDetailsTab = !!isIndexMatch || !!isDetailsTabMatch
  const isDocumentsTab = !!isDocumentsTabMatch

  const [isShowing, setIsShowing] = useState(true)

  const [getClassName, makeTestId] = useNameFormatter('DashboardDetailsDrawer')
  const { track } = useWebTracking()

  const onCloseDrawer = () => {
    if (isDocumentsTab) {
      track('se_signup__application_dashboard_documents_tab__closed')
    }

    track('se_signup__application_dashboard_application_details_drawer_close__fired')
    setIsShowing(false)
  }

  const { data } = useGetCreditApplicationDetails({
    variables: {
      creditApplicationId: salesforceCreditId,
    },
    skip: !salesforceCreditId,
  })

  const legalBusinessName = data?.creditApplication?.businessInfo?.legalBusinessName || ''

  return (
    <Drawer
      activeViewKey='default'
      className={`${styles[getClassName('')]} fs-unmask`}
      dataTestId={makeTestId(salesforceCreditId)}
      isShowing={isShowing}
      views={{
        default: (
          <DrawerView
            title={legalBusinessName}
            onCloseClick={onCloseDrawer}
          >
            <div className={styles[getClassName('drawer-view')]}>
              <div className={styles[getClassName('tabs')]}>
                <Tab
                  isActive={isDetailsTab}
                  onClick={() => {
                    track('se_signup__application_dashboard_application_details_tab_drawer__fired')
                    navigate(
                      `${PATHNAME_CONSTS.DASHBOARD_PATH}/${salesforceCreditId}/${PATHNAME_CONSTS.DASHBOARD_PATH_DETAILS_DRAWER}`,
                    )
                  }}
                >
                  <FormattedMessage
                    defaultMessage='Application details'
                    id='sputnik.DashboardDetailsDrawer__QD4yJl'
                  />
                </Tab>
                <Tab
                  isActive={isDocumentsTab}
                  onClick={() => {
                    track('se_signup__application_dashboard_application_details_drawer_documents__fired')
                    navigate(
                      `${PATHNAME_CONSTS.DASHBOARD_PATH}/${salesforceCreditId}/${PATHNAME_CONSTS.DASHBOARD_PATH_DOCUMENTS_DRAWER}`,
                    )
                  }}
                >
                  <FormattedMessage
                    defaultMessage='Documents'
                    id='sputnik.DashboardDetailsDrawer__vBlT6y'
                  />
                </Tab>
              </div>
              <div className={styles[getClassName('content')]}>
                <Suspense
                  key={pathname}
                  fallback={<Spinner centeredWithAbsolutePositioning />}
                >
                  <Outlet />
                </Suspense>
              </div>
            </div>
          </DrawerView>
        ),
      }}
      onCloseComplete={() => navigate(PATHNAME_CONSTS.DASHBOARD_PATH)}
    />
  )
}

export default DashboardDetailsDrawer
